import type { User } from "firebase/auth";
import { type CSSProperties, useEffect, useState } from "react";
import { avatarHue, gravatarUrl, initials } from "./avatar";
import { go } from "./hooks";
import { knownName } from "./memberNames";
import { Dropdown } from "./Menu";
import { HOW_TO, menuWay, useInstall } from "./offline/install";
import { useVersionText, versionTitle } from "./offline/update";

const failed = new Set<string>();

/** The circle with the initials, and the Gravatar image on top of it; a clear image lets the initials show. */
export function Avatar({ user, size = 32 }: { user: User; size?: number }) {
  const email = user.email ?? "";
  const [url, setUrl] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let live = true;
    setLoaded(false);
    void gravatarUrl(email)
      .then((found) => {
        if (live) setUrl(found && !failed.has(found) ? found : null);
      })
      .catch(() => console.info("[avatar] no SHA-256 in this browser; initials shown"));
    return () => {
      live = false;
    };
  }, [email]);
  return (
    <span
      className="avatar"
      data-testid="avatar"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.42), "--avatar-hue": avatarHue(email) } as CSSProperties}
      aria-hidden="true"
    >
      <span className="avatar-initials">{initials(email, user.displayName || knownName(email))}</span>
      {url && (
        <img
          src={url}
          alt=""
          width={size}
          height={size}
          referrerPolicy="no-referrer"
          hidden={!loaded}
          onLoad={() => setLoaded(true)}
          onError={() => {
            if (!failed.has(url)) console.info("[avatar] the Gravatar image did not load; initials shown");
            failed.add(url);
            setUrl(null);
          }}
        />
      )}
    </span>
  );
}

/** Install app, for a browser with no install event: a button that opens its how-to. */
function InstallHowTo() {
  const { way } = useInstall();
  const [open, setOpen] = useState(false);
  if (!menuWay(way)) return null;
  return (
    <>
      <button
        type="button"
        className="account-install"
        data-testid="account-menu-install"
        data-way={way}
        aria-expanded={open}
        aria-controls="account-install-how"
        onClick={() => setOpen(!open)}
      >
        Install app
      </button>
      {open && (
        <p id="account-install-how" className="account-install-how" data-testid="account-menu-install-how">
          {HOW_TO[way]}
        </p>
      )}
    </>
  );
}

/** The avatar at the right of the header: the name and email, Your settings, Install app, the version and Sign out. */
export function AccountMenu({ user, base, onSignOut }: { user: User; base: string; onSignOut: () => void }) {
  const version = useVersionText();
  const email = user.email ?? "";
  const name = user.displayName || knownName(email);
  return (
    <Dropdown className="account-menu" testId="account-menu" label={`Your settings: ${email}`} summary={<Avatar user={user} />}>
      {(close) => (
        <div className="account-panel">
          <div className="account-who">
            <Avatar user={user} size={40} />
            <div>
              {name && <strong className="account-name">{name}</strong>}
              <span className="account-address" data-testid="header-email">
                {email}
              </span>
            </div>
          </div>
          <a
            href={`${base}/settings#your-account`}
            data-testid="account-menu-settings"
            onClick={(event) => {
              go(event);
              close();
            }}
          >
            Your settings
          </a>
          <InstallHowTo />
          <button type="button" className="account-sign-out" data-testid="sign-out" onClick={onSignOut}>
            Sign out
          </button>
          <p className="account-version" data-testid="app-version" title={versionTitle()}>
            {version}
          </p>
        </div>
      )}
    </Dropdown>
  );
}
